import catalogo from "./ubicacionesDGI.json";

/**
 * Las ubicaciones de la DGI: provincia, distrito y corregimiento.
 *
 * La factura electrónica de Panamá identifica dónde está el cliente con un código
 * compuesto, `P-D-C` --`8-8-7` es Bella Vista, en el distrito de Panamá--: es el
 * `dCodUbi` (campo B4051 de la ficha técnica), y Business Central lo manda desde
 * `EXCPANCodigo Ubicacion FE` de la ficha del cliente. Si ese campo está vacío, la
 * migración de Panamá no registra la factura: le hace `TestField` al armar el XML
 * (`Codeunit 57000 - Facturacion Electronica Panama.al:783`).
 *
 * **En Business Central no hay catálogo.** El campo es un `Code[20]` libre, sin
 * tabla relacionada, así que un código que no existe llega a la DGI tal cual. El
 * catálogo vive aquí, sacado de la fuente oficial: las tablas 23, 24 y 25 de la
 * ficha técnica de la FE para PAC, versión 1.00 de abril de 2025. Se extrajeron
 * con `12 - Panamá/test-2026-09-17/catalogo-ubicaciones-dgi.mjs`, que comprueba que
 * todo corregimiento tenga su distrito y su provincia: 13 provincias, 81 distritos
 * y 651 corregimientos activos. Los seis que la tabla marca «Inactivo» se dejaron
 * fuera, porque no se pueden elegir.
 */

const porCodigo = (lista) => new Map(lista.map(({ codigo, nombre }) => [codigo, nombre]));

const PROVINCIAS = porCodigo(catalogo.provincias);
const DISTRITOS = porCodigo(catalogo.distritos);
const CORREGIMIENTOS = porCodigo(catalogo.corregimientos);

/** Cómo se escribe un código de ubicación: tres números separados por guiones. */
export const FORMA_DEL_CODIGO = /^\d{1,2}-\d{1,2}-\d{1,2}$/;

/**
 * La ubicación de un código, con los tres nombres.
 *
 * @param {string} codigo  Por ejemplo `"8-8-7"`.
 * @returns {{codigo: string, provincia: string, distrito: string, corregimiento: string}|null}
 *   `null` si el código no es un corregimiento activo del catálogo.
 */
export const ubicacionDelCodigo = (codigo) => {
  const limpio = String(codigo ?? "").trim();
  const corregimiento = CORREGIMIENTOS.get(limpio);
  if (!corregimiento) return null;

  const [provincia, distrito] = limpio.split("-");
  return {
    codigo: limpio,
    provincia: PROVINCIAS.get(provincia),
    distrito: DISTRITOS.get(`${provincia}-${distrito}`),
    corregimiento,
  };
};

/** Los tres niveles, del que abarca más al que menos: `["8", "8-8", "8-8-7"]`. */
export const nivelesDelCodigo = (codigo) => {
  const limpio = String(codigo ?? "").trim();
  if (!FORMA_DEL_CODIGO.test(limpio)) return [];
  const [p, d] = limpio.split("-");
  return [p, `${p}-${d}`, limpio];
};

// Los códigos se ordenan por sus números, no como texto: «8-10» va después de «8-8».
const numerosDe = (codigo) => codigo.split("-").map(Number);
const porNumero = (a, b) => {
  const x = numerosDe(a.value);
  const y = numerosDe(b.value);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
  }
  return 0;
};

let arbol = null;

/**
 * El catálogo como árbol, para un selector en cascada: provincia → distrito →
 * corregimiento. Se arma una vez.
 *
 * @returns {Array<{value: string, label: string, children: Array}>}
 */
export const opcionesDeUbicacion = () => {
  if (arbol) return arbol;

  const provincias = new Map(
    [...PROVINCIAS].map(([value, label]) => [value, { value, label, children: [] }])
  );
  const distritos = new Map();
  for (const [value, label] of DISTRITOS) {
    const nodo = { value, label, children: [] };
    distritos.set(value, nodo);
    provincias.get(value.split("-")[0])?.children.push(nodo);
  }
  for (const [value, label] of CORREGIMIENTOS) {
    const [p, d] = value.split("-");
    distritos.get(`${p}-${d}`)?.children.push({ value, label });
  }

  const ordenar = (nodos) =>
    nodos.sort(porNumero).map((n) => (n.children ? { ...n, children: ordenar(n.children) } : n));
  arbol = ordenar([...provincias.values()]);
  return arbol;
};

/** Cuántas hay de cada una. Lo usan las pruebas para notar un catálogo recortado. */
export const tamanoDelCatalogo = () => ({
  provincias: PROVINCIAS.size,
  distritos: DISTRITOS.size,
  corregimientos: CORREGIMIENTOS.size,
});
